import { Fragment, useContext, useEffect, useState } from 'react';
import {
  Tooltip,
  Drawer,
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Theme,
  useTheme
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem, GridValueGetterParams } from '@mui/x-data-grid';
import { MetadataError } from '@ep/error-handling';
import { EsgCategory, MetricGroup, Scope, Standard } from '@esg/esg-global-types';
import type {
  CreateMetricClientData,
  ResolverMutationRequestMetadata,
  UpdateMetricClientData
} from '@esg/syntiro-api-core';
import { ReactComponent as CreateIcon } from '../../../assets/icons/Create.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/Edit.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/Delete.svg';
import DeleteConfirmModal from '../../shared/modal/DeleteConfirmModal';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import {
  getMetricCategoryFromStandard,
  getStandards,
  getStandardsMasterList
} from '../../../lib/app/standard';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
// getMetrics/getUnconfiguredMetrics (and the other reads below) stay on v1 deliberately: v1's
// resolveScope attaches `scope.reference`, which v2's serializeMetric strips. Migrating these reads
// would feed a reference-less scope into the v2 `updateMetric` guard below and turn every ordinary
// company-level edit into a thrown error (GE-2907 migration plan, Finding 27).
import {
  getMetrics,
  MetricExtended,
  metric_label,
  deleteMetricWithEmissionFactors,
  allowDeleteMetric,
  getMetricByName,
  undeleteMetricWithEmissionFactors,
  MetricData,
  getUnconfiguredMetrics
} from '../../../lib/metric_capture/metric';
// createMetrics/updateMetric are the v2 clients — the v1 createMetrics/updateMetric wrappers above
// (in lib/metric_capture/metric) are left in place, uncalled, as the rollback path (GE-2907
// migration plan PR 2/PR 3 rollout notes).
import { createMetrics, updateMetric } from '../../../graphql/services/metric.service';
import StandardSelect from '../../shared/input/select/StandardSelect';
import { PanelTabs, PanelTabsWrapper } from '../../shared/tab/PanelTabsWrapper';
import MetricSelect from '../../shared/input/select/MetricSelect';
import MetricGroupSelect from '../../shared/input/select/MetricGroupSelect';
import { Heading } from '../../shared/typography/PrimaryTypography';
import DataGridLoading from '../../shared/datagrid/DataGridLoading';
import { UserContext } from '../../../context/UserContext';
import DataGridNoRows from '../../shared/datagrid/DataGridNoRows';
import DataGridToolbar from '../../shared/datagrid/DataGridToolbar';
import ScopeSelect from '../../shared/input/select/ScopeSelect';
import {
  ENTITIES_REQUIRED_NON_MASTERLIST,
  FEEDBACK_ACTION_FAILURE,
  FUNCTION_UNKNOWN_FAILURE
} from '../../../util/global_variables';
import ConfigEntityForm, { ConfigEntityFormType, ConfigEntityInput } from '../ConfigEntityForm';
import { useSubscriptionContext } from '../../../context/SubscriptionContext';
import { ExceededQuotaKey } from '../../../lib/app/subscription';
import { withScopeReference } from '../../../lib/app/scope';
import { replicateMasterListEmissionFactorsToCompany } from '../../../graphql/services/emission_factor.service';
import { formatTitleCase } from '../../../util/general_utils';

export interface MetricGridRow extends Omit<MetricExtended, 'primary_metric_calculated'> {
  standard: Standard;
  primary_metric_calculated?: MetricExtended;
}

interface PanelMetricGroupsProps {
  master_list?: boolean;
}

// Input schema types.
interface MasterListMetricInputData {
  [key: string]: MasterListMetricInputData[keyof MasterListMetricInputData];
  standard?: Standard | null;
  metric_group?: MetricGroup | null;
  primary_metric_calculated?: MetricExtended | null;
  name: string;
  description: string;
  accumulation_method?: 'accumulated' | 'change_of_state';
  scope?: Scope;
  unit?: string;
}

interface ConfiguredMetricsInputData {
  [key: string]: ConfiguredMetricsInputData[keyof ConfiguredMetricsInputData];
  configured_standards: Array<Standard>;
  master_list_metrics: Array<MetricExtended>;
  glo_id?: string;
}

const SOURCE = 'PanelMetrics.tsx';

/**
 * Maps a v1 `MetricData`/`MetricExtended` local shape (used by the three create call sites below)
 * to the v2 `CreateMetricClientData` wire shape. `id` is only carried through when present on the
 * input — absent for master-list primary/calculated creates, present for company-level bulk
 * creates of already-known master-list metrics.
 * @param {MetricData | MetricExtended} metric Local metric data being submitted for creation.
 * @returns {CreateMetricClientData}
 */
export const buildCreateMetricClientData = (
  metric: MetricData | MetricExtended
): CreateMetricClientData => {
  const standard = metric.standard as Standard;
  const metric_group = metric.metric_group as MetricGroup;
  return {
    // `'id' in metric` is a *structural* check standing in for a *semantic* one ("is this a
    // master-list metric being added to a company"), correct today only because the sole
    // id-bearing object reaching this path is a master-list metric. A company `MetricExtended`
    // (e.g. re-creating from an existing row) also has `.id`, and the server would build
    // `master_list_metric` pointing at a company metric id — a dangling reference nothing
    // validates server-side. Preserved from v1's identical check; not guarded here on purpose.
    ...('id' in metric && metric.id ? { id: metric.id } : {}),
    name: metric.name,
    description: metric.description,
    unit: metric.unit,
    // `?? ''`, not a bare pass-through: v1's read path coalesces a missing esg_category to
    // `null`, but the wire type is non-nullable `String!` — a `null` here fails GraphQL variable
    // coercion before the resolver runs (an opaque, unlogged error). Routing it through as `''`
    // instead reaches the server's own named guard ("<name> has no esg_category..."), which
    // throws a diagnosable, server-logged error naming the metric.
    esg_category: metric.esg_category ?? '',
    deleted: metric.deleted,
    ...(metric.accumulation_method && { accumulation_method: metric.accumulation_method }),
    is_calculated: metric.is_calculated,
    metric_group: {
      id: metric_group.id,
      name: metric_group.name,
      description: metric_group.description
    },
    standard: {
      id: standard.id,
      name: standard.name,
      is_quantitative: standard.is_quantitative,
      require_emission_factor: standard.require_emission_factor
    },
    scope: metric.scope as CreateMetricClientData['scope'],
    ...(metric.primary_metric_calculated && {
      primary_metric_calculated: { id: (metric.primary_metric_calculated as { id: string }).id }
    })
  };
};

/**
 * Datagrid handling the display of configured or Master List Metrics with relative Standard data.
 * @param {boolean | undefined} master_list Control whether metric data being manipulated is in master list or company configuration.
 * @returns {JSX.Element}
 */
const PanelMetrics = ({ master_list }: PanelMetricGroupsProps) => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const { user } = useContext(UserContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);
  const [metricRows, setMetricRows] = useState<Array<MetricGridRow>>([]);
  const [actionRow, setActionRow] = useState<MetricGridRow | null>(null);
  const [configEntityFormType, setConfigEntityFormType] = useState<ConfigEntityFormType | null>(
    null
  );
  const [masterListMetricInputData, setMasterListMetricInputData] =
    useState<MasterListMetricInputData | null>(null);
  const [configuredMetricsInputData, setConfiguredMetricsInputData] =
    useState<ConfiguredMetricsInputData | null>(null);
  const [unconfiguredMetrics, setUnconfiguredMetrics] = useState<Array<MetricExtended>>([]);
  const [loadingUnconfiguredMetrics, setloadingUnconfiguredMetrics] = useState<boolean>(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [allowMetricDelete, setAllowMetricDelete] = useState<boolean | null>(null);
  const [gridLoading, setGridLoading] = useState(true);
  const theme: Theme = useTheme();
  const primary_blue: string = theme.palette.primary.main;

  const { incrementSubscriptionUsage, decrementSubscriptionUsage, getExceededSubscriptionKeys } =
    useSubscriptionContext();

  const exceeded_subscription_keys: Array<ExceededQuotaKey> = getExceededSubscriptionKeys([
    'e_metrics',
    's_g_metrics'
  ]);

  // Get options to display for configured metric creation based on selected standards.
  const selected_standard_master_list_ids: Array<string> = configuredMetricsInputData
    ? configuredMetricsInputData.configured_standards.map(
        (standard: Standard) => standard.master_list_standard?.id ?? ''
      )
    : [];
  const filtered_unconfigured_metric_options: Array<MetricExtended> = unconfiguredMetrics.filter(
    (metric: MetricExtended) => selected_standard_master_list_ids.includes(metric.standard.id)
  );

  // Input schema declarations with required node factories.
  const primary_metric_inputs: Array<ConfigEntityInput<MasterListMetricInputData>> = [
    {
      id: 'standard',
      type: 'node',
      label: 'Standard',
      hide_input: configEntityFormType === 'edit',
      inputNodeFactory: (handleInputChange) => (
        <StandardSelect
          master_list
          handleChangeStandards={(standards: Array<Standard> | Standard | null) => {
            if (master_list && !Array.isArray(standards)) {
              handleInputChange('standard', standards);
              if (masterListMetricInputData)
                setMasterListMetricInputData({ ...masterListMetricInputData, standard: standards });
            }
          }}
        />
      )
    },
    {
      id: 'metric_group',
      type: 'node',
      label: 'Metric Group',
      hide_input: configEntityFormType === 'edit',
      inputNodeFactory: (handleInputChange) => (
        <MetricGroupSelect
          master_list
          handleChangeMetricGroups={(metric_groups: Array<MetricGroup> | MetricGroup | null) => {
            if (master_list && !Array.isArray(metric_groups)) {
              handleInputChange('metric_group', metric_groups);
              if (masterListMetricInputData)
                setMasterListMetricInputData({
                  ...masterListMetricInputData,
                  metric_group: metric_groups
                });
            }
          }}
        />
      )
    },
    {
      id: 'name',
      type: 'text',
      label: 'Name',
      onControlledInputChange: (input_value: unknown) => {
        if (typeof input_value === 'string' && masterListMetricInputData)
          setMasterListMetricInputData({ ...masterListMetricInputData, name: input_value });
      }
    },
    {
      id: 'description',
      type: 'text',
      label: 'Description',
      onControlledInputChange: (input_value: unknown) => {
        if (typeof input_value === 'string' && masterListMetricInputData)
          setMasterListMetricInputData({ ...masterListMetricInputData, description: input_value });
      }
    },
    {
      id: 'accumulation_method',
      type: 'node',
      label: 'Metric Accumulated?',
      default_value: 'accumulated',
      ignore_banned_characters: true,
      optional: masterListMetricInputData?.standard?.is_quantitative !== true,
      hide_input: masterListMetricInputData?.standard?.is_quantitative !== true,
      inputNodeFactory: (handleInputChange) => (
        <FormControl>
          <FormLabel>Accumulation Method</FormLabel>
          <RadioGroup
            row
            defaultValue={
              masterListMetricInputData
                ? masterListMetricInputData.accumulation_method
                : 'accumulated'
            }
            value={
              masterListMetricInputData
                ? (masterListMetricInputData.accumulation_method ?? 'accumulated')
                : undefined
            }
            onChange={(event, value: string) => {
              if (value === 'accumulated' || value === 'change_of_state') {
                handleInputChange('accumulation_method', value);
              }
            }}
          >
            <FormControlLabel value="accumulated" control={<Radio />} label="Accumulated" />
            <FormControlLabel value="change_of_state" control={<Radio />} label="Change of State" />
          </RadioGroup>
        </FormControl>
      ),
      onControlledInputChange: (input_value: unknown) => {
        if (
          typeof input_value === 'string' &&
          (input_value === 'accumulated' || input_value === 'change_of_state') &&
          masterListMetricInputData
        )
          setMasterListMetricInputData({
            ...masterListMetricInputData,
            accumulation_method: input_value
          });
      }
    },
    {
      id: 'scope',
      type: 'node',
      label: 'Scope',
      ignore_banned_characters: true,
      optional: masterListMetricInputData?.standard?.require_emission_factor !== true,
      hide_input: masterListMetricInputData?.standard?.require_emission_factor !== true,
      inputNodeFactory: (handleInputChange) => (
        <ScopeSelect
          selected_options={masterListMetricInputData?.scope}
          handleChangeSelection={(scope: Scope | Array<Scope> | null) => {
            if (master_list && !Array.isArray(scope) && masterListMetricInputData) {
              handleInputChange('scope', scope);
              setMasterListMetricInputData({
                ...masterListMetricInputData,
                scope: scope ? scope : undefined
              });
            }
          }}
        />
      )
    },
    {
      id: 'unit',
      type: 'text',
      label: 'Unit',
      optional: masterListMetricInputData?.standard?.is_quantitative !== true,
      hide_input: masterListMetricInputData?.standard?.is_quantitative !== true,
      onControlledInputChange: (input_value: unknown) => {
        if (typeof input_value === 'string' && masterListMetricInputData)
          setMasterListMetricInputData({ ...masterListMetricInputData, unit: input_value });
      }
    }
  ];

  const calculated_metric_inputs: Array<ConfigEntityInput<MasterListMetricInputData>> = [
    {
      id: 'primary_metric_calculated',
      type: 'node',
      label: 'Primary Metric',
      hide_input: configEntityFormType === 'edit',
      inputNodeFactory: (handleInputChange) => (
        <MetricSelect
          master_list
          input_label="Primary Metric"
          quantitative_only
          primary_metrics_only
          handleChangeMetrics={(metrics: Array<MetricExtended> | MetricExtended | null) => {
            if (master_list && !Array.isArray(metrics)) {
              handleInputChange('primary_metric_calculated', metrics);
              if (masterListMetricInputData)
                setMasterListMetricInputData({
                  ...masterListMetricInputData,
                  primary_metric_calculated: metrics
                });
            }
          }}
        />
      )
    },
    {
      id: 'name',
      type: 'text',
      label: 'Name',
      onControlledInputChange: (input_value: unknown) => {
        if (typeof input_value === 'string' && masterListMetricInputData)
          setMasterListMetricInputData({ ...masterListMetricInputData, name: input_value });
      }
    },
    {
      id: 'description',
      type: 'text',
      label: 'Description',
      onControlledInputChange: (input_value: unknown) => {
        if (typeof input_value === 'string' && masterListMetricInputData)
          setMasterListMetricInputData({ ...masterListMetricInputData, description: input_value });
      }
    },
    {
      id: 'scope',
      type: 'node',
      label: 'Scope',
      inputNodeFactory: (handleInputChange) => (
        <ScopeSelect
          selected_options={masterListMetricInputData?.scope}
          handleChangeSelection={(scope: Scope | Array<Scope> | null) => {
            if (master_list && !Array.isArray(scope)) {
              handleInputChange('scope', scope);
              if (masterListMetricInputData)
                setMasterListMetricInputData({
                  ...masterListMetricInputData,
                  scope: scope ? scope : undefined
                });
            }
          }}
        />
      ),
      optional:
        masterListMetricInputData?.standard?.require_emission_factor !== true &&
        (masterListMetricInputData?.primary_metric_calculated?.standard as Standard | undefined)
          ?.require_emission_factor !== true,
      hide_input:
        masterListMetricInputData?.standard?.require_emission_factor !== true &&
        (masterListMetricInputData?.primary_metric_calculated?.standard as Standard | undefined)
          ?.require_emission_factor !== true
    },
    {
      id: 'unit',
      type: 'text',
      label: 'Unit',
      optional: !masterListMetricInputData?.primary_metric_calculated?.unit,
      hide_input: !masterListMetricInputData?.primary_metric_calculated?.unit,
      onControlledInputChange: (input_value: unknown) => {
        if (typeof input_value === 'string' && masterListMetricInputData)
          setMasterListMetricInputData({ ...masterListMetricInputData, unit: input_value });
      }
    }
  ];

  const configured_metrics_inputs: Array<ConfigEntityInput<ConfiguredMetricsInputData>> = [
    {
      id: 'configured_standards',
      type: 'node',
      label: 'Standards',
      optional: configEntityFormType !== 'create',
      hide_input: configEntityFormType !== 'create',
      inputNodeFactory: (handleInputChange) => (
        <StandardSelect
          master_list={false}
          multi_select
          group_id={group?.id ?? undefined}
          company_id={company?.id ?? undefined}
          handleChangeStandards={(standards: Array<Standard> | Standard | null) => {
            if (configuredMetricsInputData && Array.isArray(standards)) {
              handleInputChange('configured_standards', standards);
              if ((standards as Array<Standard>).length < 1) {
                handleInputChange('master_list_metrics', null);
                setConfiguredMetricsInputData({
                  ...configuredMetricsInputData,
                  master_list_metrics: []
                });
              } else {
                setConfiguredMetricsInputData({
                  ...configuredMetricsInputData,
                  configured_standards: standards
                });
              }
            }
          }}
        />
      )
    },
    {
      id: 'master_list_metrics',
      type: 'node',
      label: 'Metrics',
      optional: configEntityFormType !== 'create',
      hide_input: configEntityFormType !== 'create',
      inputNodeFactory: (handleInputChange) => (
        <MetricSelect
          options={filtered_unconfigured_metric_options}
          selected_options={configuredMetricsInputData?.master_list_metrics ?? []}
          tag_limit={5}
          multi_select={true}
          group_by="standard.name"
          handleChangeMetrics={(metrics) => {
            if (!master_list && Array.isArray(metrics)) {
              handleInputChange('metrics', metrics);
              if (configuredMetricsInputData)
                setConfiguredMetricsInputData({
                  ...configuredMetricsInputData,
                  master_list_metrics: metrics
                });
            }
          }}
          disable_selected_tags
          loading={loadingUnconfiguredMetrics}
        />
      )
    },
    {
      id: 'glo_id',
      type: 'text',
      label: 'GLO ID',
      optional: true,
      ignore_banned_characters: true,
      hide_input: !user?.super_admin || configEntityFormType !== 'edit',
      onControlledInputChange: (input_value: unknown) => {
        if (typeof input_value === 'string' && configuredMetricsInputData)
          setConfiguredMetricsInputData({ ...configuredMetricsInputData, glo_id: input_value });
      }
    }
  ];

  // Handler functions
  const handleCreateClick = (): void => {
    if (master_list) {
      const default_master_list_metric_input_data: MasterListMetricInputData = {
        standard: null,
        metric_group: null,
        primary_metric_calculated: null,
        name: '',
        description: '',
        accumulation_method: 'accumulated',
        scope: undefined,
        unit: undefined
      };
      setMasterListMetricInputData(default_master_list_metric_input_data);
    } else {
      const default_configured_create_metrics: ConfiguredMetricsInputData = {
        configured_standards: [],
        master_list_metrics: []
      };
      setConfiguredMetricsInputData(default_configured_create_metrics);
    }
    setActionRow(null);
    setConfigEntityFormType('create');
  };

  const handleEditClick = (metric: MetricGridRow): void => {
    if (master_list) {
      const master_list_metric_input_data: MasterListMetricInputData = {
        standard: metric.standard,
        metric_group: metric.metric_group,
        name: metric.name,
        description: metric.description,
        accumulation_method: metric.accumulation_method,
        scope: metric.scope ? metric.scope : undefined,
        unit: metric.unit ? metric.unit : undefined,
        primary_metric_calculated: metric.primary_metric_calculated
      };
      setMasterListMetricInputData(master_list_metric_input_data);
    } else {
      setConfiguredMetricsInputData({
        configured_standards: [],
        master_list_metrics: [],
        glo_id: metric.glo_id
      });
    }
    setActionRow(metric);
    setConfigEntityFormType('edit');
  };

  const handleDeleteClick = async (metric_row: MetricGridRow): Promise<void> => {
    try {
      if (group && company) {
        setActionRow(metric_row);
        setAllowMetricDelete(
          await allowDeleteMetric(group.id, company.id, metric_row.id, metric_row.metric_group.id)
        );
        setShowDeleteModal(true);
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'handleClickClick'),
          {
            group: group,
            company: company,
            'metric_row.id': metric_row.id,
            metric_group_id: metric_row.metric_group.id
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to delete metric. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  const handleCloseDeleteModal = (): void => {
    setShowDeleteModal(false);
  };

  const handleCloseConfigForm = (): void => {
    setConfigEntityFormType(null);
  };

  const fetchUnconfiguredMetrics = async (): Promise<void> => {
    try {
      if (!group || !company) {
        throw new Error(ENTITIES_REQUIRED_NON_MASTERLIST(['group', 'company']));
      }
      setloadingUnconfiguredMetrics(true);
      const unconfigured_metrics: Array<MetricExtended> = await getUnconfiguredMetrics(
        group?.id,
        company?.id,
        undefined,
        true
      );
      setUnconfiguredMetrics(unconfigured_metrics);
      setloadingUnconfiguredMetrics(false);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error
          ? err.message
          : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'fetchUnconfiguredMetrics'),
        {
          group: group,
          company: company
        },
        tracking_id
      );
    }
  };

  // Row functions
  const fetchRows = async (): Promise<void> => {
    setGridLoading(true);
    try {
      setUnconfiguredMetrics([]);
      if (!master_list && (!group || !company)) {
        throw new Error(ENTITIES_REQUIRED_NON_MASTERLIST(['group', 'company']));
      }
      // Load rows from database.
      const [metrics, standards]: [Array<MetricExtended>, Array<Standard>] = await Promise.all(
        master_list
          ? [getMetrics(true), getStandardsMasterList()]
          : [getMetrics(false, group!.id, company!.id), getStandards(group!.id, company!.id)]
      ).catch((err: unknown) => {
        throw new Error(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'fetchRows')
        );
      });
      const metric_rows: Array<MetricGridRow> = [];
      metrics.forEach((metric: MetricExtended) => {
        const standard: Standard | null =
          standards.find((standard: Standard) => standard.id === metric.standard.id) ?? null;
        let primary_metric: MetricExtended | undefined = undefined;
        if (metric.primary_metric_calculated) {
          primary_metric = metrics.find(
            (primary_check_metric: MetricExtended) =>
              primary_check_metric.id === metric.primary_metric_calculated?.id
          );
        }
        if (standard) {
          metric_rows.push({
            ...metric,
            standard: standard,
            primary_metric_calculated: primary_metric
          });
        }
      });
      await fetchUnconfiguredMetrics();
      // Load rows to memory.
      setMetricRows(metric_rows);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'fetchRows'),
          {
            group: group,
            company: company
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to fetch metrics', tracking_id),
        state: true,
        type: 'error'
      });
    } finally {
      setGridLoading(false);
    }
  };

  // Create rows from various input data schemas.
  const createConfiguredMetricRows = async (): Promise<void> => {
    try {
      if (!configuredMetricsInputData || !group || !company) {
        throw new Error('Missing required input data');
      }
      // Check whether each of the selected metrics have been previously deleted, then undelete the metrics and its emission factors accordingly.
      const metrics_create: Array<MetricExtended> = [];
      const metric_promises: Array<Promise<void>> =
        configuredMetricsInputData.master_list_metrics.map(async (metric: MetricExtended) => {
          const metric_delete: MetricExtended | undefined = await getMetricByName(
            false,
            metric.metric_group.name,
            metric.name,
            group?.id,
            company?.id,
            true
          );
          if (metric_delete) {
            await undeleteMetricWithEmissionFactors(
              group?.id ? group.id : '',
              company?.id ? company.id : '',
              metric_delete.id,
              metric_delete.metric_group.id
            );
          } else {
            // GE-2907 fix: reference-bearing scope rather than a raw passthrough. This is the bulk
            // company-level create path — the origin of the "andbeyond scope nulls" defect, since
            // metric.scope here can be a reference-less object (ScopeSelect fetches via v2, which
            // omits `reference`) and the v2 createMetrics guard now refuses to silently persist
            // scope: null for that shape. withScopeReference is numeric-hardened (GE-2907 migration
            // plan Finding 26), so this is safe even for a legacy numeric scope.
            metrics_create.push({ ...metric, scope: withScopeReference(metric.scope) });
          }
        });
      await Promise.all(metric_promises);
      if (metrics_create.length > 0) {
        const client_data: Array<CreateMetricClientData> = metrics_create.map(
          buildCreateMetricClientData
        );
        const created_metric_ids: Array<string> | null | undefined = await createMetrics(
          { master_list: false, group_id: group.id, company_id: company.id },
          client_data,
          { include_emission_factors: false, create_audit_log: true, return_data: true }
        );
        // A shorter-than-requested result is a real partial-success signal (e.g. a metric with a
        // missing metric_group/standard is silently skipped server-side, no throw) — surface it
        // rather than reporting the same "success" for 300/300 and 250/300.
        if ((created_metric_ids?.length ?? 0) < client_data.length) {
          setFeedbackData({
            message: `Only ${created_metric_ids?.length ?? 0} of ${client_data.length} metrics were created. Check the metrics for missing group/standard data and try again.`,
            state: true,
            type: 'warning'
          });
        }
        metrics_create.forEach((metric) => {
          incrementSubscriptionUsage([`${metric.esg_category}_metrics`]);
          if (['s', 'g'].includes(metric.esg_category)) {
            incrementSubscriptionUsage(['s_g_metrics']);
          }
        });
        if (!master_list) {
          await replicateMasterListEmissionFactorsToCompany({
            group_id: group.id,
            company_id: company.id
          });
        }
      }
      fetchRows();
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error
          ? err.message
          : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'createRowsFromMasterList'),
        {
          group: group,
          company: company
        },
        tracking_id
      );
    }
  };

  const createPrimaryMetricRow = async (): Promise<void> => {
    try {
      if (!masterListMetricInputData) {
        throw new Error('Missing required input data');
      }
      const { standard, metric_group, name, description, accumulation_method, scope, unit } =
        masterListMetricInputData;
      if (!standard || !metric_group || !name || !description) {
        throw new Error('Missing required input data');
      }
      const esg_category: EsgCategory = getMetricCategoryFromStandard(standard);
      const metric_data: MetricData = {
        deleted: null,
        name: name,
        description: description,
        accumulation_method: standard.is_quantitative
          ? !accumulation_method
            ? 'accumulated'
            : accumulation_method
          : undefined,
        scope: standard.require_emission_factor && scope ? withScopeReference(scope) : null,
        unit: standard.is_quantitative && unit ? unit : null,
        standard: standard,
        metric_group: metric_group,
        esg_category: esg_category,
        is_calculated: false
      };
      // v2's return can legitimately be an empty array or fewer ids than requested on partial
      // success (Finding 16 — v1's return type lied about this), so the grid refresh is
      // unconditional here rather than gated on a truthy result.
      const created_metric_ids: Array<string> | null | undefined = await createMetrics(
        { master_list: true },
        [buildCreateMetricClientData(metric_data)],
        { return_data: true }
      );
      if ((created_metric_ids?.length ?? 0) === 0) {
        setFeedbackData({
          message: `${name} was not created. Check its group and standard data and try again.`,
          state: true,
          type: 'warning'
        });
      }
      setMetricRows([]);
      await fetchRows();
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'createPrimaryMetricRow'),
          {
            masterListMetricInputData: masterListMetricInputData
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to create metrics'),
        state: true,
        type: 'error'
      });
    }
  };

  const createCalculatedMetricRow = async (): Promise<void> => {
    try {
      if (!masterListMetricInputData) {
        throw new Error('Missing required input data');
      }
      const { primary_metric_calculated, name, description, scope, unit } =
        masterListMetricInputData;
      if (!primary_metric_calculated || !name || !description) {
        throw new Error('Missing required input data');
      }
      const metric_data: MetricData = {
        deleted: null,
        name: name,
        description: description,
        scope: withScopeReference(scope),
        unit: unit ?? null,
        standard: primary_metric_calculated.standard,
        metric_group: primary_metric_calculated.metric_group,
        esg_category: primary_metric_calculated.esg_category,
        primary_metric_calculated: primary_metric_calculated,
        is_calculated: true
      };
      // Unconditional refresh — see the identical comment in createPrimaryMetricRow (Finding 16).
      const created_metric_ids: Array<string> | null | undefined = await createMetrics(
        { master_list: true },
        [buildCreateMetricClientData(metric_data)],
        { return_data: true }
      );
      if ((created_metric_ids?.length ?? 0) === 0) {
        setFeedbackData({
          message: `${name} was not created. Check its group and standard data and try again.`,
          state: true,
          type: 'warning'
        });
      }
      setMetricRows([]);
      await fetchRows();
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'createCalculatedMetricRow'),
          {
            masterListMetricInputData: masterListMetricInputData
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to create metrics'),
        state: true,
        type: 'error'
      });
    }
  };

  // Transform input schema fields to grid rows.
  const transformMasterListMetricInputToRow = (
    input_data: MasterListMetricInputData
  ): MetricGridRow | undefined => {
    try {
      if (!actionRow) {
        throw new Error('Missing selected row for update');
      }
      return {
        id: actionRow.id,
        deleted: actionRow.deleted,
        standard: actionRow.standard,
        metric_group: actionRow.metric_group,
        name: input_data.name,
        description: input_data.description,
        accumulation_method: input_data.accumulation_method,
        scope: withScopeReference(input_data.scope),
        unit: input_data.unit ?? null,
        esg_category: actionRow.esg_category
      };
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'transformMasterListMetricInputToRow'),
          {
            actionRow: actionRow,
            input_data: input_data
          },
          tracking_id
        )
      );
    }
  };

  const transformConfiguredMetricInputToRow = (
    input_data: ConfiguredMetricsInputData
  ): MetricGridRow | undefined => {
    try {
      if (!actionRow) {
        throw new Error('Missing selected row for update');
      }
      return {
        id: actionRow.id,
        deleted: actionRow.deleted,
        standard: actionRow.standard,
        metric_group: actionRow.metric_group,
        name: actionRow.name,
        description: actionRow.description,
        accumulation_method: actionRow.accumulation_method,
        // Hardened, not a raw passthrough: actionRow.scope can legitimately be a legacy numeric
        // scope (Finding 26), and the v2 updateMetric guard now rejects a reference-less scope
        // object rather than silently nulling it (GE-2907) — a raw `?? null` would strip
        // `.reference` off nothing here, but leaving the passthrough unguarded is the exact origin
        // of the bug 0b38f15c fixes.
        scope: withScopeReference(actionRow.scope) ?? null,
        unit: actionRow.unit ?? null,
        esg_category: actionRow.esg_category,
        glo_id: input_data.glo_id
      };
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'transformConfiguredMetricInputToRow'),
          {
            actionRow: actionRow,
            input_data: input_data
          },
          tracking_id
        )
      );
    }
  };

  const updateRow = async (
    updated_row: MetricGridRow,
    original_data: MetricGridRow
  ): Promise<void> => {
    try {
      // master_list is sent explicitly and group/company are omitted entirely in that mode — the
      // master-list edit screen still carries group?.id/company?.id in context even while editing a
      // master-list row, and inferring master_list from their presence would pick the wrong branch
      // server-side (GE-2907 migration plan, Finding 19).
      const metadata: ResolverMutationRequestMetadata = master_list
        ? { master_list: true }
        : { master_list: false, group_id: group?.id, company_id: company?.id };
      const client_data: UpdateMetricClientData = {
        id: updated_row.id,
        metric_group_id: updated_row.metric_group.id,
        standard_id: updated_row.standard.id,
        name: updated_row.name,
        description: updated_row.description,
        unit: updated_row.unit,
        scope: updated_row.scope,
        esg_category: updated_row.esg_category,
        deleted: updated_row.deleted,
        accumulation_method: updated_row.accumulation_method,
        glo_id: updated_row.glo_id,
        // original_metric is opaque to the type system (kept on the wire for audit-log parity
        // with v1) — the row's concrete shape doesn't satisfy Record<string, unknown>'s index
        // signature, so an explicit widening cast is required here.
        original_metric: original_data as unknown as Record<string, unknown>
      };
      await updateMetric(metadata, client_data, { create_audit_log: true }).then(async () => {
        setMetricRows([]);
        await fetchRows();
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'updateRow'),
          {
            master_list: master_list,
            group: group,
            company: company,
            updated_row: updated_row,
            original_data: original_data
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to update metric', tracking_id),
        state: true,
        type: 'error'
      });
    }
  };

  // Delete row entity in database.
  const deleteRow = async (row: MetricGridRow): Promise<void> => {
    try {
      if (group && company) {
        // Soft delete Metric database record.
        await deleteMetricWithEmissionFactors(
          !!master_list,
          row.id,
          row.metric_group.id,
          row.name,
          group.id,
          company.id
        ).catch((err: unknown) => {
          throw new Error(
            err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'deleteRow')
          );
        });

        decrementSubscriptionUsage(['e_metrics']);

        // Remove from memory.
        setMetricRows(metricRows.filter((obj) => obj.id !== row.id));
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'deleteRow'),
          {
            group: group,
            company: company,
            row: row
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to delete metric. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  useEffect(
    () => {
      try {
        (async () => {
          setMetricRows([]);
          fetchRows();
        })().catch((error) => {
          throw new Error(error);
        });
      } catch (err: unknown) {
        const tracking_id: string = generateUuidV4();
        log(
          'error',
          new MetadataError(
            err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'fetchRows'),
            null,
            tracking_id
          )
        );
        setFeedbackData({
          message: `An error occurred. Tracking ID: ${tracking_id}`,
          state: true,
          type: 'error'
        });
      }
      return;
    },
    master_list ? [] : [group, company]
  );

  // Datagrid row columns.
  const columns: GridColDef[] = [
    {
      field: 'standard_name',
      headerName: 'Standard',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (params: GridValueGetterParams) => {
        return params.row.standard.name;
      },
      flex: 1
    },
    {
      field: 'standard_is_quantitative',
      headerName: 'Type',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (params: GridValueGetterParams) => {
        return params.row.standard.is_quantitative ? 'Quantitative' : 'Qualitative';
      },
      flex: 1
    },
    {
      field: 'group_name',
      headerName: 'Category',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (params: GridValueGetterParams) => {
        return params.row.metric_group.name;
      },
      flex: 1
    },
    {
      field: 'name',
      headerName: 'Name',
      headerAlign: 'left',
      align: 'left',
      flex: 1
    },
    {
      field: 'unit',
      headerName: 'Unit',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (params: GridValueGetterParams) => {
        return params.row.standard.is_quantitative ? params.value : 'Text';
      },
      flex: 1
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      headerAlign: 'right',
      align: 'right',
      hideable: false,
      flex: 1,
      getActions: ({ row }) => {
        return [
          <>
            {(master_list || user?.super_admin) && (
              <GridActionsCellItem
                key={1}
                icon={
                  <Tooltip title="Edit Metric">
                    <EditIcon width={25} fill={theme.palette.primary.main} />
                  </Tooltip>
                }
                size="large"
                label="Edit"
                sx={{
                  color: 'primary.main'
                }}
                onClick={() => handleEditClick(row)}
                disabled={row.locked}
              />
            )}
            <GridActionsCellItem
              key={2}
              icon={
                <Tooltip title="Delete metric">
                  <DeleteIcon width={25} fill={theme.palette.primary.main} />
                </Tooltip>
              }
              size="large"
              label="Delete"
              sx={{
                color: 'primary.main'
              }}
              onClick={() => handleDeleteClick(row)}
            />
          </>
        ];
      }
    }
  ];

  return (
    <>
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        allow_delete={allowMetricDelete}
        allow_archive={false}
        open={showDeleteModal}
        handleCloseDeleteModal={handleCloseDeleteModal}
        item_type={'Metric'}
        delete_warning={
          'You will be required to create this Metric again if deleted. All Emission Factors configured for this Metric will also be deleted'
        }
        delete_label={actionRow && `${actionRow.metric_group.name}: ${actionRow.name}`}
        handleDelete={actionRow && (() => deleteRow(actionRow))}
      />

      {/* Side Widget Panel */}
      <Fragment key={'right'}>
        <Drawer
          anchor={'right'}
          open={!!configEntityFormType}
          onClose={handleCloseConfigForm}
          PaperProps={{ style: { width: '40%', padding: '1.5rem' } }}
        >
          {/* Create entity based on various input schemas */}
          {configEntityFormType === 'create' && master_list && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  mb: 4
                }}
              >
                <CreateIcon height={40} fill={primary_blue} stroke={primary_blue} />
                <Heading sx={{ mx: 2 }}>Add {metric_label.many}</Heading>
              </Box>
              <PanelTabsWrapper>
                <PanelTabs label="Primary" disabled={false}>
                  {masterListMetricInputData && (
                    <ConfigEntityForm<MasterListMetricInputData>
                      inputs={primary_metric_inputs}
                      controlled_entity={masterListMetricInputData}
                      handleClose={handleCloseConfigForm}
                      handleConfirmInput={createPrimaryMetricRow}
                    />
                  )}
                </PanelTabs>
                <PanelTabs label={'Calculated'} disabled={false}>
                  {masterListMetricInputData && (
                    <ConfigEntityForm<MasterListMetricInputData>
                      inputs={calculated_metric_inputs}
                      controlled_entity={masterListMetricInputData}
                      handleClose={handleCloseConfigForm}
                      handleConfirmInput={createCalculatedMetricRow}
                    />
                  )}
                </PanelTabs>
              </PanelTabsWrapper>
            </>
          )}
          {configEntityFormType === 'create' && !master_list && configuredMetricsInputData && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  mb: 4
                }}
              >
                <CreateIcon height={40} fill={primary_blue} stroke={primary_blue} />
                <Heading sx={{ mx: 2 }}>Add {metric_label.many}</Heading>
              </Box>
              <PanelTabsWrapper>
                <PanelTabs label="Generic" disabled={false}>
                  <ConfigEntityForm<ConfiguredMetricsInputData>
                    inputs={configured_metrics_inputs}
                    controlled_entity={configuredMetricsInputData}
                    handleClose={handleCloseConfigForm}
                    handleConfirmInput={createConfiguredMetricRows}
                  />
                </PanelTabs>
                <PanelTabs label={'Custom'} disabled={true} />
              </PanelTabsWrapper>
            </>
          )}
          {/* Edit entity based on various input schemas */}
          {configEntityFormType === 'edit' &&
            master_list &&
            masterListMetricInputData &&
            actionRow && (
              <ConfigEntityForm<MasterListMetricInputData>
                form_title={`Edit ${formatTitleCase(metric_label.one)}`}
                form_icon={<EditIcon width={40} fill={primary_blue} />}
                controlled_entity={masterListMetricInputData}
                inputs={actionRow.is_calculated ? calculated_metric_inputs : primary_metric_inputs}
                handleClose={handleCloseConfigForm}
                handleConfirmInput={async (input_metric_data: MasterListMetricInputData) => {
                  const updated_metric_row: MetricGridRow | undefined =
                    transformMasterListMetricInputToRow(input_metric_data);
                  updated_metric_row && (await updateRow(updated_metric_row, actionRow));
                }}
              />
            )}
          {configEntityFormType === 'edit' &&
            !master_list &&
            configuredMetricsInputData &&
            actionRow && (
              <ConfigEntityForm<ConfiguredMetricsInputData>
                form_title={`Edit ${formatTitleCase(metric_label.one)}`}
                form_icon={<EditIcon width={40} fill={primary_blue} />}
                controlled_entity={configuredMetricsInputData}
                inputs={configured_metrics_inputs}
                handleClose={handleCloseConfigForm}
                handleConfirmInput={async (input_metric_data: ConfiguredMetricsInputData) => {
                  const updated_metric_row: MetricGridRow | undefined =
                    transformConfiguredMetricInputToRow(input_metric_data);
                  updated_metric_row && (await updateRow(updated_metric_row, actionRow));
                }}
              />
            )}
        </Drawer>
      </Fragment>

      {/* Interactive Data Table */}
      <DataGrid
        autoHeight
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 }
          }
        }}
        pageSizeOptions={[10, 25, 50, 100]}
        columnHeaderHeight={metricRows.length < 1 ? 0 : 56}
        loading={gridLoading}
        rows={metricRows}
        columns={columns}
        disableRowSelectionOnClick
        slots={{
          toolbar: () => (
            <DataGridToolbar
              entity_label={metric_label}
              disable_export={metricRows.length === 0}
              handleCreate={handleCreateClick}
              disable_create={!master_list && !!exceeded_subscription_keys.length}
            />
          ),
          noRowsOverlay: () => <DataGridNoRows missing_label={metric_label} />,
          loadingOverlay: () => <DataGridLoading columns={columns.length} />
        }}
        slotProps={{ toolbar: { handleCreate: handleCreateClick, buttonDisabled: gridLoading } }}
        sx={{ '&, [class^=MuiDataGrid]': { border: 'none' } }}
      />
    </>
  );
};

export default PanelMetrics;
